import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './FormResponsesComponents.scss';

interface DraggableSliderProps {
	children: React.ReactNode;
	cardWidth?: number;
}

export const DraggableSlider: React.FC<DraggableSliderProps> = ({ children, cardWidth = 340 }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [scrollPosition, setScrollPosition] = useState(0);
	const [maxScroll, setMaxScroll] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);

	const checkScrollability = () => {
		if (containerRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
			setScrollPosition(scrollLeft);
			setMaxScroll(scrollWidth - clientWidth);
		}
	};

	const handleScroll = (amount: number) => {
		if (containerRef.current) {
			containerRef.current.scrollBy({ left: amount, behavior: 'smooth' });
		}
	};

	const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (containerRef.current) {
			setIsDragging(true);
			setStartX(e.pageX - containerRef.current.offsetLeft);
			setScrollLeft(containerRef.current.scrollLeft);
			containerRef.current.style.cursor = 'grabbing';
		}
	};

	const onMouseLeave = () => {
		if (containerRef.current) {
			setIsDragging(false);
			containerRef.current.style.cursor = 'grab';
		}
	};

	const onMouseUp = () => {
		if (containerRef.current) {
			setIsDragging(false);
			containerRef.current.style.cursor = 'grab';
		}
	};

	const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!isDragging || !containerRef.current) {
			return;
		}
		e.preventDefault();
		const x = e.pageX - containerRef.current.offsetLeft;
		const walk = (x - startX) * 2;
		containerRef.current.scrollLeft = scrollLeft - walk;
	};

	useEffect(() => {
		const currentRef = containerRef.current;
		const timer = setTimeout(() => checkScrollability(), 100);
		if (currentRef) {
			currentRef.addEventListener('scroll', checkScrollability);
			window.addEventListener('resize', checkScrollability);
		}
		return () => {
			clearTimeout(timer);
			if (currentRef) {
				currentRef.removeEventListener('scroll', checkScrollability);
				window.removeEventListener('resize', checkScrollability);
			}
		};
	}, [children]);

	const scrollbarWidth =
		maxScroll > 0
			? (containerRef.current!.clientWidth / containerRef.current!.scrollWidth) * 100
			: 100;
	const scrollbarLeft = maxScroll > 0 ? (scrollPosition / maxScroll) * (100 - scrollbarWidth) : 0;

	return (
		<div className="slider-container">
			{scrollPosition > 0 && (
				<Button
					shape="circle"
					icon={<LeftOutlined />}
					onClick={() => handleScroll(-cardWidth * 2)}
					className="slider-button left"
				/>
			)}
			<div
				ref={containerRef}
				className="slider-content"
				onMouseDown={onMouseDown}
				onMouseLeave={onMouseLeave}
				onMouseUp={onMouseUp}
				onMouseMove={onMouseMove}
			>
				{children}
			</div>
			{scrollPosition < maxScroll - 1 && (
				<Button
					shape="circle"
					icon={<RightOutlined />}
					onClick={() => handleScroll(cardWidth * 2)}
					className="slider-button right"
				/>
			)}
			{maxScroll > 0 && (
				<div className="custom-scrollbar-track">
					<div
						className="custom-scrollbar-thumb"
						style={{ width: `${scrollbarWidth}%`, left: `${scrollbarLeft}%` }}
					/>
				</div>
			)}
		</div>
	);
};
